import { useEffect, useState } from "react";
import { BadgeCheck, Building2, Check, ClipboardCheck, PlusCircle, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatChatDate } from "@/lib/format";
import { useLanguage } from "@/i18n/LanguageContext";

const STATUS_STYLES = {
  pending: { label: "En attente", classes: "bg-amber-50 text-amber-600" },
  approved: { label: "Approuvée", classes: "bg-emerald-50 text-emerald-600" },
  rejected: { label: "Refusée", classes: "bg-red-50 text-red-500" },
};

export const AdminClaims = ({ onDecision }) => {
  const { t, lang } = useLanguage();
  const [claims, setClaims] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/claims").then((r) => setClaims(r.data)).catch(() => setClaims([]));
  useEffect(() => { load(); }, []);

  const decide = async (claim, action) => {
    setBusy(claim.id);
    try {
      await api.post(`/admin/claims/${claim.id}/${action}`);
      toast.success(action === "approve" ? "Demande approuvée" : "Demande refusée — l'établissement a été délié du compte");
      load();
      onDecision?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  if (claims === null) {
    return <div className="mt-6 h-64 animate-pulse rounded-3xl bg-white" />;
  }

  const pending = claims.filter((c) => c.status === "pending");
  const done = claims.filter((c) => c.status !== "pending");

  const ClaimRow = ({ c }) => (
    <div data-testid={`claim-${c.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4">
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${c.type === "creation" ? "bg-[#FAF5FF]" : "bg-[#EFF6FF]"}`}>
        {c.type === "creation" ? <PlusCircle className="h-5 w-5 text-[#9333EA]" /> : <Building2 className="h-5 w-5 text-[#2563EB]" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
          {c.establishment_name}
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">
            {c.type === "creation" ? "Nouvelle création" : "Revendication"}
          </span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_STYLES[c.status].classes}`}>
            {STATUS_STYLES[c.status].label}
          </span>
        </p>
        <p className="mt-0.5 text-xs text-slate-400">
          {c.user_name} ({c.user_email}) • {c.category_label} • {c.neighborhood} • {formatChatDate(c.created_at, lang)}
        </p>
      </div>
      {c.status === "pending" && (
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            data-testid={`claim-approve-${c.id}`}
            disabled={busy === c.id}
            onClick={() => decide(c, "approve")}
            className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold text-white transition hover:bg-emerald-600 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
            Approuver
          </button>
          <button
            type="button"
            data-testid={`claim-reject-${c.id}`}
            disabled={busy === c.id}
            onClick={() => decide(c, "reject")}
            className="flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-4 py-2 text-xs font-semibold text-red-500 transition hover:bg-red-50 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
            Refuser
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="mt-6 space-y-6" data-testid="admin-claims">
      <div>
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
          <ClipboardCheck className="h-4 w-4 text-[#2563EB]" />
          Demandes en attente
          {pending.length > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">{pending.length}</span>
          )}
        </h2>
        <div className="mt-3 space-y-2.5">
          {pending.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-400">
              {t("Aucune demande en attente. Les revendications et créations d'établissements Pro apparaîtront ici.")}
            </p>
          ) : (
            pending.map((c) => <ClaimRow key={c.id} c={c} />)
          )}
        </div>
      </div>

      {done.length > 0 && (
        <div>
          <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
            <BadgeCheck className="h-4 w-4 text-emerald-500" />
            Historique des demandes
          </h2>
          <div className="mt-3 space-y-2.5">
            {done.map((c) => <ClaimRow key={c.id} c={c} />)}
          </div>
        </div>
      )}
    </div>
  );
};
